import React, { useState, useEffect, useRef } from "react";
import { Select } from "antd";
import { getPharmacyItems } from "../../../../apis/pharmacyItems";
import { createPOSSale } from "../../../../apis/pos";
import { PharmacyItem } from "../../../../interfaces/PharmacyItems";
import { PharmacyBatch } from "../../../../interfaces/POS";
import { useNotification } from "../../../../NotificationContext";
import { Visit } from "../../../../interfaces/Visit";
const { Option } = Select;

interface PharmacyRequestFormProps {
  initialData?: any;
  visit: Visit;
  onSave: (data: any) => void;
  onCancel: () => void;
}

interface RequestItem {
  pharmacyItemId: number;
  name: string;
  quantity: number;
  status: string;
  batchId?: number;
  mrp?: number;
  sellingPrice?: number;
}

const PharmacyRequestForm: React.FC<PharmacyRequestFormProps> = ({
  initialData,
  visit,
  onSave,
  onCancel,
}) => {
  const { showNotification } = useNotification();
  const [pharmacyItems, setPharmacyItems] = useState<PharmacyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSubmittingRef = useRef(false);
  const [formData, setFormData] = useState({
    items: (initialData?.items || []) as RequestItem[],
  });
  const [newItem, setNewItem] = useState<RequestItem>({
    pharmacyItemId: 0,
    name: "",
    quantity: 1,
    status: "pending",
  });

  useEffect(() => {
    fetchPharmacyItems();
  }, []);

  const fetchPharmacyItems = async () => {
    try {
      setLoading(true);
      const response: any = await getPharmacyItems({ per_page: 1000, is_active: true, has_batches: true });
      // Handle different response structures: could be array, or { data: [...], links: {...}, meta: {...} }
      const items = Array.isArray(response) ? response : response?.data || [];
      console.log("Fetched pharmacy items response:", response);
      console.log("Extracted items:", items);
      console.log("First item:", items[0]);
      console.log("First item batches:", items[0]?.batches);
      setPharmacyItems(items || []);
    } catch (error) {
      console.error("Failed to fetch pharmacy items:", error);
      showNotification(
        "error",
        "Error",
        "Failed to fetch pharmacy items",
        "topRight"
      );
    } finally {
      setLoading(false);
    }
  };

  const addOrMergeItem = (item: RequestItem) => {
    if (!item.pharmacyItemId || item.quantity <= 0 || !item.batchId) return;
    setFormData((prev) => {
      const existingIndex = prev.items.findIndex(
        (i) => i.pharmacyItemId === item.pharmacyItemId && i.batchId === item.batchId
      );
      if (existingIndex >= 0) {
        const updatedItems = [...prev.items];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: updatedItems[existingIndex].quantity + item.quantity,
        };
        return { ...prev, items: updatedItems };
      }
      return { ...prev, items: [...prev.items, { ...item }] };
    });
  };

  const handlePharmacyItemSelect = (pharmacyItemId: number) => {
    const selectedItem = pharmacyItems.find((item) => item.id === pharmacyItemId);
    console.log({selectedItem});
    
    if (selectedItem) {
      // Use batches from the item if available
      const batches = selectedItem.batches || [];
      console.log("Selected item:", selectedItem.name);
      console.log("Item batches:", batches);
      
      // Find first available batch (not expired, has quantity, and is active)
      const availableBatch = batches.find(
        (batch: any) => {
          const isNotExpired = !(batch.isExpired ?? batch.is_expired);
          const availableQty = batch.availableQuantity ?? batch.available_quantity ?? 0;
          const hasQuantity = Number(availableQty) > 0;
          const isActive = (batch.isActive ?? batch.is_active) !== false && (batch.isActive ?? batch.is_active) !== 0;
          return isNotExpired && hasQuantity && isActive;
        }
      );
      
      if (!availableBatch) {
        console.log("No available batch found. All batches:", batches);
        showNotification(
          "warning",
          "No Available Batch",
          `Item "${selectedItem.name}" does not have an available batch. Please select another item.`,
          "topRight"
        );
        // Reset the select to prevent adding item without batch
        setNewItem({
          pharmacyItemId: 0,
          name: "",
          quantity: 1,
          status: "pending",
          batchId: undefined,
          sellingPrice: undefined,
        });
        return;
      }

      const batchId = availableBatch.id;
      const sellingPriceStr = (availableBatch as any).sellingPrice ?? (availableBatch as any).selling_price ?? "0";
      const sellingPrice = parseFloat(String(sellingPriceStr)) || 0;

      const itemToAdd: RequestItem = {
        pharmacyItemId: selectedItem.id!,
        name: selectedItem.name,
        quantity: newItem.quantity || 1,
        status: "pending",
        batchId,
        sellingPrice,
      };
      addOrMergeItem(itemToAdd);
      setNewItem({
        pharmacyItemId: 0,
        name: "",
        quantity: 1,
        status: "pending",
        batchId: undefined,
        sellingPrice: undefined,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent multiple submissions
    if (isSubmittingRef.current || saving) {
      return;
    }
    
    if (formData.items.length === 0) {
      showNotification(
        "warning",
        "Validation Error",
        "Please add at least one item",
        "topRight"
      );
      return;
    }

    isSubmittingRef.current = true;
    setSaving(true);

    try {
      // Get current user from localStorage
      const user = JSON.parse(window.localStorage.getItem("user") || "{}");
      const userId = user?.id;

      if (!userId) {
        showNotification(
          "error",
          "Error",
          "User not found. Please login again.",
          "topRight"
        );
        setSaving(false);
        return;
      }

      // Get patient ID from visit
      const patientId = visit.patientId || visit.patient?.id;
      if (!patientId) {
        showNotification(
          "error",
          "Error",
          "Patient ID not found in visit",
          "topRight"
        );
        setSaving(false);
        return;
      }

      // Get current date
      const saleDate = new Date().toISOString().split("T")[0];

      // Validate that all items have batch IDs
      const itemsWithoutBatch = formData.items.filter((item) => !item.batchId);
      if (itemsWithoutBatch.length > 0) {
        showNotification(
          "error",
          "Validation Error",
          "Some items are missing batch information. Please remove and re-add them.",
          "topRight"
        );
        setSaving(false);
        return;
      }

      // Convert request items to POS sale items format
      const saleItems = formData.items.map((item) => {
        if (!item.batchId) {
          throw new Error(`Item ${item.name} is missing batch ID`);
        }
        return {
          pharmacy_item_id: item.pharmacyItemId,
          pharmacy_batch_id: item.batchId, // Required field - cannot be null
          quantity: item.quantity,
          mrp: item.mrp || 0,
          selling_price: item.sellingPrice || 0,
          discount_percentage: 0,
          tax_percentage: 0,
        };
      });

      // Prepare sale data matching backend API structure
      const saleData = {
        patient_id: patientId,
        visit_id: visit.id || null,
        doctor_id: userId,
        sale_date: saleDate,
        discount_percentage: 0,
        status: "pending", // Set status to pending for requests
        notes: "Pharmacy Request from IPD",
        items: saleItems,
      };

      // Call createPOSSale API
      const response: any = await createPOSSale(saleData);
      const pharmacySaleId = response?.data?.id || response?.data?.data?.id || response?.id;
      
      // Pass data to onSave - parent IPDEntryForm will create the visit entry
      // Only call onSave once (check that we're still in submitting state)
      if (isSubmittingRef.current) {
        onSave({
          pharmacy_sale_id: pharmacySaleId,
          notes: "Pharmacy Request from IPD",
        });
      }
    } catch (error: any) {
      console.error("Failed to save pharmacy request:", error);
      const errorMessage =
        error?.response?.data?.message || "Failed to save pharmacy request";
      showNotification("error", "Error", errorMessage, "topRight");
    } finally {
      isSubmittingRef.current = false;
      setSaving(false);
    }
  };

  const removeItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_: RequestItem, i: number) => i !== index),
    });
  };

  const updateItemQuantity = (index: number, quantity: number) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      quantity: Math.max(1, quantity),
    };
    setFormData({
      ...formData,
      items: updatedItems,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Request Items
        </label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          <Select
            style={{ width: "100%" }}
            placeholder="Select Pharmacy Item (adds to list)"
            value={newItem.pharmacyItemId && newItem.batchId ? newItem.pharmacyItemId : undefined}
            onChange={handlePharmacyItemSelect}
            loading={loading}
            showSearch
            filterOption={(input, option) => {
              const children = option?.children;
              const text = typeof children === 'string' ? children : String(children);
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            className="col-span-2"
            allowClear
          >
            {pharmacyItems.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name} {item.form && `(${item.form})`}
                {item.strength && ` - ${item.strength}`}
              </Option>
            ))}
          </Select>
          <input
            type="number"
            min="1"
            value={newItem.quantity}
            onChange={(e) =>
              setNewItem({
                ...newItem,
                quantity: parseInt(e.target.value) || 1,
              })
            }
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
            placeholder="Qty to add"
            title="Quantity to add when you select an item (same item selected again will add this quantity)"
          />
        </div>
        {formData.items.length > 0 && (
          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Item Name
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Quantity
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {formData.items.map((item: RequestItem, index: number) => (
                  <tr key={index}>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          updateItemQuantity(
                            index,
                            parseInt(e.target.value) || 1
                          )
                        }
                        className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm"
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        {item.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || formData.items.length === 0}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Save Request"}
        </button>
      </div>
    </form>
  );
};

export default PharmacyRequestForm;
